import { writePelotonConsentCookie } from './pelotonConsent';

// States that have privacy policy rules not allowing geolocation fetching without explicit permission
export enum ProtectedStates {
  CALIFORNIA = 'CA',
  COLORADO = 'CO',
  CONNECTICUT = 'CT',
  NEVADA = 'NV',
  UTAH = 'UT',
  VIRGINIA = 'VA',
  WASHINGTON = 'WA',
  MONTANA = 'MT',
  TEXAS = 'TX',
  OREGON = 'OR',
}

export enum ProtectedCountry {
  UK = 'GB',
}

interface Segment {
  load: Function;
  page: Function;
  track: Function;
  identify: Function;
  group: Function;
  ready: Function;
  reset: Function;
  user: Function;
}

export const checkIfProd = (appName: string) => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.location.href.match(`${appName}.onepeloton.com`)) {
    return true;
  }
  return false;
};

export const doNotSellLink = 'https://preferences.onepeloton.com';
export const isProtectedState = (state: string | undefined): boolean => {
  if (!state) {
    return false;
  }
  const upperState = state.toUpperCase();
  return Object.values(ProtectedStates).includes(upperState as ProtectedStates);
};

export const isProtectedCountry = (country: string | undefined): boolean => {
  if (!country) {
    return false;
  }
  const upperCountry = country.toUpperCase();
  return Object.values(ProtectedCountry).includes(upperCountry as ProtectedCountry);
};

export const hasAnalyticsFn = (name: keyof Segment): boolean =>
  // hasAnalytics(window) &&
  window.analytics?.[name] !== undefined && typeof window.analytics[name] === 'function';

export function waitForVendor(vendor: string, timeoutMs = 20000) {
  if (typeof window === 'undefined') {
    return;
  }
  return new Promise<any>((resolve, reject) => {
    const ac = new AbortController();
    const onReady = (e: Event) => {
      ac.abort();
      clearTimeout(timeoutId);
      resolve((e as CustomEvent).detail);
    };
    window.addEventListener(`vendor:${vendor}`, onReady, {
      once: true,
      signal: ac.signal,
    });
    const timeoutId = setTimeout(() => {
      ac.abort();
      reject(new Error(`vendor timeout: ${vendor}`));
    }, timeoutMs);
  });
}

const appendBlockedScripts = () => {
  if (typeof window === 'undefined' || window.toAppend?.length === 0) {
    return;
  }
  if (
    window.Fides?.consent.marketing === true ||
    window.Fides?.consent.analytics === true
  ) {
    window.toAppend?.forEach(
      node => window.document.appendChild.call(window.document, node),
      setTimeout(() => {
        if (hasAnalyticsFn('load')) {
          // @ts-expect-error
          window?.analytics?.load();
        }
      }, 1000),
    );
    window.toAppend = [];
  }
};

// https://segment.com/docs/guides/filtering-data/#filtering-with-the-integrations-object
export const getSegmentIntegrations = () => {
  if (typeof window === 'undefined' || !window.Fides) {
    return {};
  }
  const config = { integrations: {} };
  const hasAnalytics = window.Fides?.consent.analytics ?? false;
  const hasMarketing = window.Fides?.consent.marketing ?? false;
  if (!hasAnalytics) {
    const analyticsIntegrations = {
      Amplitude: false,
      Optimizely: false,
    };
    config.integrations = { ...config.integrations, ...analyticsIntegrations };
  }
  if (!hasMarketing) {
    const marketingIntegrations = {
      Facebook: false,
      'Bing Ads': false,
      'Facebook Pixel': false,
      'TikTok Pixel': false,
      'Google Tag Manager': false,
    };
    config.integrations = { ...config.integrations, ...marketingIntegrations };
  }
  return config;
};

//https://segment.com/docs/privacy/consent-management/consent-in-segment-connections/#consent-object
// manual consent management in Segment requires adding a context object to the track call
export const maybeAddSegmentContext = (context?: any) => {
  // NOTE: fidesBucket checks will be removed after OneTrust migration
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...context,
      context: {
        consent: {
          categoryPreferences: {
            functional: window?.Fides?.consent?.functional ?? false,
            social_media: window?.Fides?.consent?.social_media ?? false,
            marketing: window?.Fides?.consent?.marketing ?? false,
            analytics: window?.Fides?.consent?.analytics ?? false,
            strictly_necessary: window?.Fides?.consent?.strictly_necessary ?? false,
          },
        },
      },
    };
  } else {
    return {};
  }
};

export const getConsentIDs = (properties: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...properties,
      consentOrigin: 'fides',
      fides_id: window.Fides?.identity?.fides_user_device_id,
      uuid: crypto.randomUUID(),
    };
  } else if (typeof window !== 'undefined' && window.Optanon) {
    return {
      ...properties,
      consentOrigin: 'onetrust',
      uuid: crypto.randomUUID(),
    };
  } else {
    return properties;
  }
};

export const handleAmplitudeConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const ampInterval = setInterval(() => {
    if (window.amplitude && window.Fides && window.amplitude.getInstance) {
      clearInterval(ampInterval);
      try {
        if (window.Fides?.consent.analytics === false) {
          window.amplitude.getInstance().setOptOut(true);
        } else if (window.Fides?.consent.analytics === true) {
          window.amplitude.getInstance().setOptOut(false);
          window.amplitude.getInstance().sendEvents();
        }
      } catch (error) {
        console.warn(error);
      }
    }
  }, 500);
};

const handleTiktokConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const tiktokInterval = setInterval(() => {
    if (window.ttq && window.ttq.initialize && window.Fides) {
      clearInterval(tiktokInterval);
      if (window.Fides?.consent.marketing === false) {
        window.ttq.revokeConsent();
        window.ttq.disableCookie();
      } else {
        window.ttq.grantConsent();
        window.ttq.enableCookie();
      }
    }
  }, 500);
};

const handleFacebookConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const fbInterval = setInterval(() => {
    if (window.fbq && window.fbq.loaded && window.Fides) {
      clearInterval(fbInterval);
      if (window.Fides?.consent.marketing === false) {
        window.fbq('consent', 'revoke');
      } else {
        window.fbq('consent', 'grant');
      }
    }
  }, 500);
};

const handleMSConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const bingInterval = setInterval(() => {
    if (window.uetq && typeof window.uetq.push === 'function' && window.Fides) {
      clearInterval(bingInterval);
      if (
        window.Fides?.consent.analytics === false ||
        window.Fides?.consent.marketing === false
      ) {
        window.uetq.push('consent', 'revoke');
      } else {
        window.uetq.push('consent', 'grant');
      }
    }
  }, 500);
};

const handleClarityConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const bingInterval = setInterval(() => {
    if (window.clarity && typeof window.clarity === 'function' && window.Fides) {
      clearInterval(bingInterval);
      if (
        window.Fides?.consent.analytics === false ||
        window.Fides?.consent.marketing === false
      ) {
        window.clarity('consent', 'false');
      } else {
        window.clarity('consent', 'true');
      }
    }
  }, 500);
};

const handleGoogleConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const gaInterval = setInterval(() => {
    if (window.gtag && window.Fides) {
      clearInterval(gaInterval);
      const isFidesBlock =
        window.Fides &&
        (window.Fides.consent.marketing === false ||
          window.Fides.consent.analytics === false);
      // @ts-expect-error https://developer.mozilla.org/en-US/docs/Web/API/Navigator/globalPrivacyControl
      const isGPCBlock = window.navigator.globalPrivacyControl === true;
      if (isFidesBlock || isGPCBlock) {
        window.gtag('consent', 'update', {
          ad_storage: 'denied',
          analytics_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      } else {
        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          analytics_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
      }
    }
  }, 500);
};

const handleDDRUMConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const ddInterval = setInterval(() => {
    if (window.DD_RUM && window.Fides) {
      clearInterval(ddInterval);
      try {
        if (window.Fides?.consent.analytics === false) {
          window.DD_RUM?.stopSession();
          window.DD_RUM?.clearUser();
          window.DD_RUM?.setTrackingConsent('not-granted');
        } else if (window.Fides?.consent.analytics === true) {
          window.DD_RUM?.setTrackingConsent('granted');
          // commented out to reduce costs while we record the checkout flow
          // window.DD_RUM?.startSessionReplayRecording();
        }
      } catch (error) {
        console.warn(error);
      }
    }
  }, 500);
};

const OPTIMIZELY_OPT_OUT_KEY = 'optimizelyOptOut';
const OPTIMIZELY_STORAGE_PREFIX = 'optimizely';
// optimizelyOptOut persists the user's opt-out decision so the snippet stays
// silent on future loads — it must NOT be cleared while consent is denied.
const OPTIMIZELY_STORAGE_KEEP = [OPTIMIZELY_OPT_OUT_KEY];

const isClearableOptimizelyKey = (name: string) =>
  name.startsWith(OPTIMIZELY_STORAGE_PREFIX) && !OPTIMIZELY_STORAGE_KEEP.includes(name);

// Expire a cookie on both the host-only and parent-domain (.onepeloton.com)
// scopes — the snippet sets cookies on the parent domain, so a host-only
// delete alone fails to match.
const expireOptimizelyCookie = (name: string) => {
  const base = `${name}=;max-age=0;path=/`;
  document.cookie = `${base};SameSite=Lax;Secure`;
  document.cookie = `${base};domain=.onepeloton.com;SameSite=Lax;Secure`;
};

export const clearOptimizelyData = () => {
  // Sweep cookies by prefix (optimizelyEndUserId, optimizelySession, …) rather
  // than by hardcoded name, so any optimizely* cookie the snippet sets is caught.
  document.cookie
    .split('; ')
    .map(cookie => cookie.split('=')[0])
    .filter(isClearableOptimizelyKey)
    .forEach(expireOptimizelyCookie);

  try {
    Object.keys(window.localStorage)
      .filter(isClearableOptimizelyKey)
      .forEach(key => window.localStorage.removeItem(key));
  } catch (_) {
    // localStorage unavailable (private mode / disabled) — nothing to clear.
  }
};

// Clears the persisted opt-out decision (cookie + localStorage). Without this,
// a snippet loaded after consent is granted reads optimizelyOptOut and boots
// opted-out, so pushing optOut:false has no effect and no cookie is written.
export const clearOptimizelyOptOut = () => {
  expireOptimizelyCookie(OPTIMIZELY_OPT_OUT_KEY);
  try {
    window.localStorage.removeItem(OPTIMIZELY_OPT_OUT_KEY);
  } catch (_) {
    // localStorage unavailable (private mode / disabled) — nothing to clear.
  }
};

export const handleOptimizelyConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.Fides?.consent.analytics === false) {
    // Opt out FIRST so a live snippet (revoke path) stops writing before we
    // wipe — otherwise it re-persists optimizelyEndUserId / optimizely_data*
    // between the wipe and the opt-out, leaving the data behind.
    window['optimizely']?.push({
      type: 'optOut',
      isOptOut: true,
    });
    clearOptimizelyData();
    return;
  }

  // Consent granted: drop any persisted opt-out so the snippet (loaded after
  // consent via Head.tsx) does not boot opted-out and can write its cookie.
  clearOptimizelyOptOut();

  const optimizelyInterval = setInterval(() => {
    if (window.optimizely && window.Fides) {
      clearInterval(optimizelyInterval);
      if (window.Fides?.consent.analytics === true) {
        window['optimizely']?.push({
          type: 'optOut',
          isOptOut: false,
        });
      }
    }
  }, 500);
};

export const assertAllConsent = () => {
  handleTiktokConsent();
  handleFacebookConsent();
  handleMSConsent();
  handleGoogleConsent();
  handleDDRUMConsent();
  handleAmplitudeConsent();
  appendBlockedScripts();
  handleClarityConsent();
  handleOptimizelyConsent();
};

const persistPelotonConsent = () => {
  const consent = window.Fides?.consent;
  // Guard on analytics being explicitly resolved rather than just `consent`
  // being truthy — an object present before Fides has finished resolving
  // categories would otherwise persist a cookie missing `analytics`, which
  // checkConsent reads as Denied instead of the correct Unknown.
  if (consent && typeof consent.analytics === 'boolean') {
    writePelotonConsentCookie(consent);
  }
};

// TODO: Try to replace Intervals with script initialized checks
export const setupFidesListeners = () => {
  if (typeof window !== 'undefined') {
    // FidesInitialized fires once Fides has resolved default consent (post-geo,
    // pre-UI-interaction). This is what lets our server-side middleware see a
    // consent value on the *next* request without waiting for the user to
    // interact with the Fides modal.
    window.addEventListener('FidesInitialized', () => {
      persistPelotonConsent();
    });
    window.addEventListener('FidesReady', evt => {
      const id = window.Fides?.experience?.experience_config?.id;
      if (id) {
        window.document.body.classList.add(id);
      }
      if (window.analytics) {
        window.analytics.track('track', getConsentIDs({}), {
          ...maybeAddSegmentContext(),
          ...getSegmentIntegrations(),
        });
      }
      assertAllConsent();
    });
    window.addEventListener('FidesUpdated', evt => {
      persistPelotonConsent();
      if (window.analytics) {
        window.analytics.track('track', getConsentIDs({}), {
          ...maybeAddSegmentContext(),
          ...getSegmentIntegrations(),
        });
      }
      assertAllConsent();
      if (window.Fides?.consent.marketing || window.Fides?.consent.analytics) {
        window.dispatchEvent(new CustomEvent('vendor:fides', { detail: 'fides' }));
      }
    });
    // Guard against setupFidesListeners running after Fides already initialized —
    // in that case we've missed FidesInitialized and would leave the cookie
    // stale until the next FidesUpdated.
    if (window.Fides?.consent) {
      persistPelotonConsent();
    }
  }
};
